import React, { useEffect } from "react";
import ReactQuill from "react-quill";
import { Button, Form } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import { ACCESS_CONFIG } from "../strings";
import { hasAccess } from "../../services/access";
import PreventReload from "../funnel-form/prevent-reload";
import TextField from "../reusable-form-fields/text-field";
import { setAMPrivacyPolicy } from "../../store/slices/auth";
import { CustomCard, HorizontalDivider } from "../common-styling";
import { cleanedQuillContent } from "../../services/common-service";
import { showError, showSuccess } from "../../services/toast-service";
import ValidationMessage from "../validation-message/validation-message";
import { updateAMPrivacyPolicy } from "../../services/api-declaration";
import { getAMPrivacyPolicy } from "../../services/api-declaration";
import { PrivacyConsentModalSchema } from "../../services/validation-schema";
import "react-quill/dist/quill.snow.css";
import { useTranslation } from "react-i18next";

const PrivacyConsent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: yupResolver(PrivacyConsentModalSchema),
    defaultValues: {
      checkboxText: "",
      checkboxClickableKeyword: "",
      modalHeading: "",
      modalContent: ""
    }
  });

  const {
    reset,
    watch,
    register,
    setValue,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting }
  } = methods;

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await getAMPrivacyPolicy();

        const data = response.data.data;

        const formatted = {
          checkboxText: data.checkboxText,
          checkboxClickableKeyword: data.checkboxClickableKeyword,
          modalHeading: data.modalHeading,
          modalContent: data.modalContent
        };

        reset(formatted);
      } catch (error) {
        console.log("Error fetching policy data", error);
        showError("Failed to load privacy policy");
      }
    };

    fetchPolicy();
  }, [reset]);

  const onSubmit = async (values) => {
    try {
      const amPrivacyPolicyData = {
        checkboxText: values.checkboxText,
        checkboxClickableKeyword: values.checkboxClickableKeyword,
        modalHeading: values.modalHeading,
        modalContent: values.modalContent
      };

      const response = await updateAMPrivacyPolicy({
        amPolicyData: amPrivacyPolicyData
      });

      reset(amPrivacyPolicyData);

      showSuccess(
        response.data?.message || "Privacy policy updated successfully"
      );

      dispatch(setAMPrivacyPolicy(response.data.data.policyVersionId));
    } catch (error) {
      console.log("Error in updating policy ", error);
      showError(
        error.response?.data?.message || "Error in updating privacy policy"
      );
    }
  };

  const handleQuillChange = (content) => {
    const cleanedContent = cleanedQuillContent(content);
    setValue("modalContent", cleanedContent, { shouldDirty: true });
  };

  return (
    <div>
      <h5 className="my-4">{t("privacyPolicy.tabName")}</h5>

      <FormProvider {...methods}>
        <PreventReload />

        <form onSubmit={handleSubmit(onSubmit)}>
          <CustomCard className="py-4 px-3">
            <div className="mb-3">
              <TextField
                type="text"
                name="checkboxText"
                register={register}
                label={t("privacyPolicy.checkboxTextLabel")}
                errors={errors?.checkboxText}
                boldLabel
              />
            </div>

            <div className="mb-3">
              <TextField
                type="text"
                name="checkboxClickableKeyword"
                register={register}
                label={t("privacyPolicy.clickableKeywordLabel")}
                errors={errors?.checkboxClickableKeyword}
                boldLabel
              />
            </div>

            <div className="mb-3">
              <TextField
                type="text"
                name="modalHeading"
                register={register}
                label={t("privacyPolicy.modalHeadingLabel")}
                errors={errors?.modalHeading}
                boldLabel
              />
            </div>

            <Form.Group className="mb-3">
              <Form.Label
                className={`fw-bold ${
                  errors?.modalContent ? "text-danger" : ""
                }`}
              >
                {`${t("privacyPolicy.modalContentLabel")}${
                  errors?.modalContent ? "*" : ""
                }`}
              </Form.Label>

              <ReactQuill
                style={{ height: "200px" }}
                className="mb-5"
                theme="snow"
                value={watch("modalContent")}
                onChange={handleQuillChange}
                preserveWhitespace
              />

              {errors?.modalContent?.message && (
                <ValidationMessage error={errors.modalContent.message} />
              )}
            </Form.Group>

            {hasAccess(ACCESS_CONFIG.ADMIN_TIER) && (
              <>
                <HorizontalDivider />

                <div className="text-end">
                  <Button
                    type="submit"
                    variant="success"
                    disabled={isSubmitting || !isDirty}
                  >
                    {t("common.update")}
                  </Button>
                </div>
              </>
            )}
          </CustomCard>
        </form>
      </FormProvider>
    </div>
  );
};

export default PrivacyConsent;
